import barba from '@barba/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { splashEnter, splashExit } from '../animation/splash.js';
import HeroAnim from '../components/hero.js';
import CircuitsAnim from '../components/circuits.js';
import BuyAnim from '../components/buy.js';
import InfographicAnim from '../components/infographic.js';
import RankingAnim from '../components/ranking.js';
import StoriesSlider from '../components/stories.js';
import DetailsAnim from '../components/details.js';
import SilverstoneAnim from '../components/silverstone.js';
import MultistepAnim from '../components/multistep.js';
import ErasGallery from '../components/erasGallery.js';
import IllustrationsAnim from '../components/illustrations.js';
import DoubleInfographicAnim from '../components/doubleInfographic.js';
import VideoPlayer from '../components/video.js';

gsap.registerPlugin(ScrollTrigger);

let barbaInitialized = false;
let ctx;

export const initBarba = () => {
	if (barbaInitialized) return;
	
	barbaInitialized = true;
	
	barba.init({
		preventRunning: true,
		transitions: [
			{
				name: 'splash',
				to: {
					namespace: ['splash']
				},
				once: (data) => splashEnter(data)
			},
			{
				name: 'from splash to home',
				from: {
					namespace: ['splash']
				},
				to: {
					namespace: ['home']
				},	
				leave: (data) => splashExit(data),
				after: (data) => new HeroAnim(data.next).enter()
			},
			{
				name: 'home',
				to: {
					namespace: ['home']
				},
				once: (data) => new HeroAnim(data.next).enter()
			},
			{
				name: 'from home to splash',
				from: {
					namespace: ['home']
				},
				to: {
					namespace: ['splash']
				},
				leave: (data) => new HeroAnim(data.current).exit('splash'),
				after: (data) => splashEnter(data)
			},
			{
				name: 'language change home',
				from: {
					namespace: ['home']
				},
				to: {
					namespace: ['home']
				},
				leave: (data) => new HeroAnim(data.current).exit('self'),
				after: (data) => new HeroAnim(data.next).enter()
			},
			{
				name: 'era once',
				to: {
					namespace: ['era1', 'era2', 'era3']
				},
				once: (data) => new MultistepAnim(data.next, "self").init(),
				leave: (data) => new MultistepAnim(data.current).leave(),
				after: (data) => new MultistepAnim(data.next).init()
			},
			{
				name: 'silverstone',
				to: {
					namespace: ['silverstone']
				},
				once: (data) => new SilverstoneAnim(data.next, "self").init(),
				leave: (data) => new SilverstoneAnim(data.current).leave(),
				after: (data) => new SilverstoneAnim(data.next).init()
			},
			{
				name: 'from home to silverstone',
				from: {
					namespace: ['home']
				},
				to: {
					namespace: ['silverstone']
				},
				leave: (data) => new HeroAnim(data.current).exit(),
				after: (data) => new SilverstoneAnim(data.next).init()
			},
			{
				name: 'from silverstone to home',
				from: {
					namespace: ['silverstone']
				},
				to: {
					namespace: ['home']
				},
				leave: (data) => new SilverstoneAnim(data.current).leave('home'),
				after: (data) => new HeroAnim(data.next).enter()
			},
			{
				name: 'from era to home',
				from: {
					namespace: ['era1', 'era2', 'era3']
				},
				to: {
					namespace: ['home']
				},
				leave: (data) => new MultistepAnim(data.current).leave('home'),
				after: (data) => new HeroAnim(data.next).enter()
			},
			{
				name: 'from home to era',
				from: {
					namespace: ['home']
				},
				to: {
					namespace: ['era1', 'era2', 'era3']
				},
				leave: (data) => new HeroAnim(data.current).exit(),
				after: (data) => new MultistepAnim(data.next).init()
			},
		],
		views: [
		{
			namespace: 'home',
			afterLeave() {
				if (ctx) {
					ctx.revert();
					ctx = null;
				}
				ScrollTrigger.killAll();
			},
			afterEnter(data) {
				ScrollTrigger.enable();

				ctx = gsap.context(() => {

					const modules = [...data.next.container.querySelectorAll('[data-module]')];
					
					modules.forEach(section => {
						switch (section.dataset.module) {
							case 'circuits':
								new CircuitsAnim(section);
							break;
						case 'buy':
								new BuyAnim(section);
							break;
						case 'infographic':
								new InfographicAnim(section);
							break;
						case 'ranking':
								new RankingAnim(section);
							break;
						case 'stories':	
								new StoriesSlider(section);
							break;
						case 'details':
								new DetailsAnim(section);
							break;
						case 'video':
								new VideoPlayer(section);
							break;
						}
					});

				}, data.next.container); 
				
				if (window.innerWidth > 1024) {
					requestAnimationFrame(() => ScrollTrigger.refresh());
				}
			}
		},
		{
			namespace: 'era1',
			afterLeave() {
				if (ctx) {
					ctx.revert();
					ctx = null;
				}
				ScrollTrigger.killAll();
			},
			afterEnter(data) {
				ScrollTrigger.enable();

				ctx = gsap.context(() => {

					const modules = [...data.next.container.querySelectorAll('[data-module]')];
					
					modules.forEach(section => {

						switch (section.dataset.module) {
							case 'illustrations':
								new IllustrationsAnim(section);
								new ErasGallery(section);
							break;
						}

					});

				}, data.next.container); 
				
				requestAnimationFrame(() => ScrollTrigger.refresh());
			}
		},
		{
			namespace: 'era2',
			afterLeave() {
				if (ctx) {
					ctx.revert();
					ctx = null;
				}
				ScrollTrigger.killAll();
			},
			afterEnter(data) {
				ScrollTrigger.enable();

				ctx = gsap.context(() => {

					const modules = [...data.next.container.querySelectorAll('[data-module]')];
					
					modules.forEach(section => {

						switch (section.dataset.module) {
							case 'illustrations':
								new IllustrationsAnim(section);
								new ErasGallery(section);
							break;
						}

					});

				}, data.next.container); 
				
				requestAnimationFrame(() => ScrollTrigger.refresh());
			}
		},
		{
			namespace: 'era3',
			afterLeave() {
					if (ctx) {
					ctx.revert();
					ctx = null;
				}
				ScrollTrigger.killAll();
			},
			afterEnter(data) {
				ScrollTrigger.enable();

				ctx = gsap.context(() => {

					const modules = [...data.next.container.querySelectorAll('[data-module]')];
					
					modules.forEach(section => {

						switch (section.dataset.module) {
							case 'illustrations':
								new IllustrationsAnim(section);
								new ErasGallery(section);
							break;
							case 'doubleInfographic':
								new DoubleInfographicAnim(section);
							break;
							case 'ranking':
								new RankingAnim(section);
							break;
						}

					});

				}, data.next.container); 
				
				requestAnimationFrame(() => ScrollTrigger.refresh());
			}
		},
	]
	})

	// Update header on page transitions
	barba.hooks.afterEnter(() => {
		if (window.headerInstance) {
			window.headerInstance.updateActiveLink();
			window.headerInstance.closeMenu();
		}
	});
}